import { BossFlow } from '../combat/BossFlow';
import { EncounterFlow } from '../combat/EncounterFlow';
import { createRng } from '../combat/dice';
import { dieCard } from '../combat/diceRoster';
import { PLAYER_MAX_HP } from '../combat/player';
import { InventoryEditor } from '../combat/ui/InventoryEditor';
import { LevelUpOverlay } from '../combat/ui/LevelUpOverlay';
import { type AcquisitionSession, openAcquisition } from '../combat/ui/curationDrawer';
import type { Character, CharacterId } from '../core/character';
import { type Hex, distance, hexKey, parseHexKey } from '../core/hex';
import type { Tile, TownLifecycleState } from '../core/tile';
import { makeClickToMove } from '../input/click-to-move';
import { panDeltaForKey } from '../input/free-look';
import { ChestFlow } from '../interaction/ChestFlow';
import { EventFlow } from '../interaction/EventFlow';
import { MerchantFlow } from '../interaction/MerchantFlow';
import { PillarFlow } from '../interaction/PillarFlow';
import { ShopFlow } from '../interaction/ShopFlow';
import { TownFlow } from '../interaction/TownFlow';
import { chestSeed } from '../interaction/chestRewards';
import {
  MAP_ITEM_PRICE,
  MAP_REVEAL_RADIUS,
  type MapOffer,
  revealableHexes,
} from '../interaction/mapItem';
import { TileLifecycle } from '../lifecycle/TileLifecycle';
import { eventSeed } from '../lifecycle/dangerEvent';
import { SPAWN_CLUSTER_CELLS } from '../lifecycle/frontierRisk';
import { getTownState } from '../lifecycle/townState';
import { Movement, isPassable } from '../movement/Movement';
import { Vision } from '../movement/Vision';
import { findPath, findPathThrough } from '../movement/pathfind';
import type { Persistence } from '../persistence/Persistence';
import { SqlJsPersistence } from '../persistence/sqljs/SqlJsPersistence';
import {
  type LoadSqlJsOptions,
  type SqlJsBlobStore,
  createIndexedDbBlobStore,
} from '../persistence/sqljs/boot';
import {
  type EvilPressureWorldState,
  STABILITY_MAX,
  accumulatedEvil,
  computePressure,
  evaluateTownStability,
} from '../progression/evilPressure';
import { XP_TILE_CROSS, addXp, rollLevelUpOffers } from '../progression/xp';
import type { Renderer } from '../render/Renderer';
import { createPixiRenderer } from '../render/pixi';
import { showsRealFace } from '../render/pixi/faceColour';
import { ZOOM_STEP, clampZoom } from '../render/zoom';
import { CompassHud } from '../ui/compass/CompassHud';
import {
  TOWN_VISIBILITY_RADIUS,
  hexesWithinRadius,
  nearestUnliberatedTown,
} from '../ui/compass/compass';
import { HoverPanel } from '../ui/hover/HoverPanel';
import { DiscoveryHud } from '../ui/hud/DiscoveryHud';
import { FreeLookCue } from '../ui/hud/FreeLookCue';
import { WorldHud } from '../ui/hud/WorldHud';
import { WorldClock } from '../world/WorldClock';
import { WorldStore } from '../world/WorldStore';
import { ChunkedWorldGen } from '../worldgen/ChunkedWorldGen';
import { CharacterFlow, CharacterFlowValidationError } from './CharacterFlow';
import { type MemorySeedTarget, seedMemory } from './memorySeed';
import { renderRosterDiscoveries } from './roster-discoveries';

import sqlWasmUrl from 'sql.js/dist/sql-wasm.wasm?url';

import '../theme/theme.css';
import '../combat/ui/card-styles.css';
import '../combat/ui/card-hover-panel-styles.css';
import '../combat/ui/card-combat-overlay-styles.css';
import '../combat/ui/inventory-editor-styles.css';
import '../interaction/interaction-modal-styles.css';
import '../ui/hover/hover-styles.css';
import '../ui/hud/hud-styles.css';

/**
 * App shell — slice-1 closer (HEX-12).
 *
 * Screens:
 *   1. character flow (create / roster / retire) driven by `CharacterFlow`
 *   2. game screen — chunked world gen behind `WorldStore`, persistence behind
 *      `SqlJsPersistence`, click-to-move + vision + lifecycle + hover panel.
 *
 * State that has to outlive the renderer (persistence, character flow) lives
 * on the App instance. Per-character runtime state (world store, movement,
 * vision, renderer) is constructed when the player enters the game and torn
 * down on retire.
 */

/**
 * Hex radius in CSS pixels (HEX-14). Sized so roughly seven to nine hex rings
 * are visible vertically on a 1080p viewport. The renderer / camera derive all
 * placement from `layout.size`; bumping this is the entire viewport-zoom knob.
 */
const HEX_SIZE = 56;
/**
 * Vision disk radius (ADR-0014 §1). Centre + ring-1 + ring-2 = 19 hexes; with
 * the tier collapse the full disk renders as one "active" mode.
 */
const VISION_RADIUS = 2;
/** Same as `VISION_RADIUS` — every visible hex must have its tile materialised. */
const RENDER_BUFFER_RADIUS = 2;
/**
 * Skyline scan radius (HEX-127). Mountains within this disk but beyond the
 * vision radius surface as distant orientation silhouettes — "navigable by
 * skyline". Wider than `RENDER_BUFFER_RADIUS` so peaks read as landmarks you
 * steer toward, not tiles you've reached. Only mountain tiles are materialised
 * out here, so the wider warm stays cheap.
 *
 * HEX-132 trim 5→3. Every impassable tile is typed `'mountain'` (~64% of all
 * tiles — see ChunkedWorldGen `if (!passable) … 'mountain'`), so the scan can't
 * tell a dramatic peak from generic impassable rubble. At radius 5 the ~72-hex
 * ring is ~46 silhouettes — a near-solid grey wall, not discrete landmarks.
 * Radius 3 (~18-hex ring, ~11 silhouettes) keeps "peaks beyond reach" without
 * the wall. The proper fix — distinguishing peak-worthy mountains from generic
 * impassable terrain — needs the impassable-typing rework (HEX-130 lake-glyph
 * territory) and is out of scope here.
 */
const LANDMARK_RADIUS = 3;
/** World generation seed. Stable for the lifetime of a save (ADR-0018 §1 —
 * one endless world). */
const WORLD_SEED = 0x6e6c1;
/**
 * Pacing between auto-walk steps (HEX-70). Matched to the renderer's 180 ms
 * camera/marker smooth tween (ADR-0014 §3/§4) so each hop visually completes
 * before the next begins — the path reads as a walk, not a teleport.
 */
const AUTO_WALK_STEP_MS = 180;
/**
 * Minimum pointer travel (CSS px) before a press in free-look counts as a map
 * drag rather than a click (HEX-144). Below this a release still walks; at or
 * above it the gesture pans and the trailing tap is suppressed.
 */
const DRAG_PAN_THRESHOLD_PX = 4;

interface AppDeps {
  readonly persistence?: Persistence;
  readonly sqlJsOptions?: LoadSqlJsOptions;
  readonly blobStore?: SqlJsBlobStore;
  readonly now?: () => number;
  readonly newId?: () => CharacterId;
}

export class App {
  private readonly root: HTMLElement;
  private readonly deps: AppDeps;
  private persistence: Persistence | null = null;
  private characterFlow: CharacterFlow | null = null;
  private game: GameSession | null = null;
  private booted = false;

  constructor(root: HTMLElement, deps: AppDeps = {}) {
    this.root = root;
    this.deps = deps;
  }

  async mount(): Promise<void> {
    this.renderLoading('Booting…');
    try {
      const persistence = await this.bootPersistence();
      this.persistence = persistence;
      const flow = new CharacterFlow({
        persistence,
        now: this.deps.now ?? (() => Date.now()),
        newId: this.deps.newId ?? defaultNewId,
      });
      this.characterFlow = flow;
      this.booted = true;
      const screen = await flow.init();
      this.renderFlowScreen(screen);
    } catch (err) {
      this.renderLoading(`Boot failed: ${(err as Error).message}`);
    }
  }

  private async bootPersistence(): Promise<Persistence> {
    if (this.deps.persistence) {
      await this.deps.persistence.init();
      return this.deps.persistence;
    }
    const blobStore = this.deps.blobStore ?? createIndexedDbBlobStore();
    const persistence = new SqlJsPersistence({
      blobStore,
      locateFile: () => sqlWasmUrl,
      ...this.deps.sqlJsOptions,
    });
    await persistence.init();
    return persistence;
  }

  // ─── screens ────────────────────────────────────────────────────────────

  private renderLoading(label: string): void {
    this.root.innerHTML = '';
    const wrap = document.createElement('div');
    wrap.className = 'screen screen-loading';
    wrap.style.cssText =
      'flex:1;display:flex;align-items:center;justify-content:center;opacity:0.7;';
    wrap.textContent = label;
    this.root.appendChild(wrap);
  }

  private async renderFlowScreen(screen: ReturnType<CharacterFlow['getScreen']>): Promise<void> {
    if (screen.kind === 'create') {
      this.renderCreate();
    } else if (screen.kind === 'roster') {
      this.renderRoster(screen.characters);
    } else {
      this.renderLoading('Loading…');
    }
  }

  private renderCreate(): void {
    this.root.innerHTML = '';
    const wrap = document.createElement('div');
    wrap.className = 'screen screen-name';
    wrap.style.cssText =
      'flex:1;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:16px;';

    const title = document.createElement('h1');
    title.textContent = 'Hex6';
    title.style.cssText = 'margin:0;font-size:28px;letter-spacing:0.12em;';

    const subtitle = document.createElement('p');
    subtitle.textContent = 'Name your character';
    subtitle.style.cssText = 'margin:0;opacity:0.75;';

    const form = document.createElement('form');
    form.style.cssText =
      'display:flex;flex-direction:column;align-items:stretch;gap:8px;min-width:280px;';

    const input = document.createElement('input');
    input.type = 'text';
    input.name = 'character-name';
    input.maxLength = 24;
    input.autocomplete = 'off';
    input.placeholder = '2–24 chars · letters, digits, _ or -';
    input.style.cssText =
      'padding:10px 12px;font-size:16px;background:#15151b;color:inherit;border:1px solid #2a2a36;border-radius:6px;';

    const submit = document.createElement('button');
    submit.type = 'submit';
    submit.textContent = 'Enter the world';
    submit.style.cssText =
      'padding:10px 12px;font-size:16px;background:#222230;color:inherit;border:1px solid #34344a;border-radius:6px;cursor:pointer;';

    const error = document.createElement('div');
    error.style.cssText = 'min-height:20px;color:#e07070;font-size:13px;';
    error.setAttribute('role', 'alert');

    const back = document.createElement('button');
    back.type = 'button';
    back.textContent = 'Back to roster';
    back.style.cssText =
      'padding:6px 10px;font-size:13px;background:transparent;color:inherit;border:1px solid #2a2a36;border-radius:6px;cursor:pointer;opacity:0.7;';

    form.append(input, submit, error);
    wrap.append(title, subtitle, form);

    const graveyardLink = document.createElement('button');
    graveyardLink.type = 'button';
    graveyardLink.dataset.action = 'open-graveyard';
    graveyardLink.textContent = '⚰ Graveyard';
    graveyardLink.style.cssText =
      'padding:6px 10px;font-size:13px;background:transparent;color:inherit;border:1px solid #2a2a36;border-radius:6px;cursor:pointer;opacity:0.7;';
    graveyardLink.addEventListener('click', () => {
      void this.renderGraveyard();
    });

    // Show "Back to roster" only when the active roster is non-empty (i.e. we got
    // here by clicking "New character"); show the Graveyard link whenever any
    // character ever existed — including when all are fallen (HEX-65).
    if (this.persistence) {
      void this.persistence.listCharacters().then((existing) => {
        if (existing.length > 0) wrap.appendChild(back);
      });
      void this.persistence.listAllCharacters().then((all) => {
        if (all.length > 0) wrap.appendChild(graveyardLink);
      });
    }

    this.root.appendChild(wrap);
    input.focus();

    back.addEventListener('click', async () => {
      if (!this.characterFlow) return;
      const characters = await this.characterFlow.persistence.listCharacters();
      if (characters.length === 0) return;
      this.renderRoster(characters);
    });

    form.addEventListener('submit', async (e) => {
      e.preventDefault();
      if (!this.characterFlow) return;
      try {
        const character = await this.characterFlow.createCharacter(input.value);
        await this.enterGame(character);
      } catch (err) {
        if (err instanceof CharacterFlowValidationError) {
          error.textContent = err.message;
        } else {
          error.textContent = (err as Error).message;
        }
      }
    });
  }

  private renderRoster(characters: readonly Character[]): void {
    this.root.innerHTML = '';
    const wrap = document.createElement('div');
    wrap.className = 'screen screen-roster';
    wrap.style.cssText =
      'flex:1;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:16px;padding:24px;';

    const title = document.createElement('h1');
    title.textContent = 'Hex6';
    title.style.cssText = 'margin:0;font-size:28px;letter-spacing:0.12em;';

    const subtitle = document.createElement('p');
    subtitle.textContent = 'Choose a character';
    subtitle.style.cssText = 'margin:0;opacity:0.75;';

    const list = document.createElement('ul');
    list.style.cssText =
      'list-style:none;margin:0;padding:0;display:flex;flex-direction:column;gap:8px;min-width:320px;';

    for (const character of characters) {
      list.appendChild(this.renderRosterRow(character));
    }

    const newButton = document.createElement('button');
    newButton.type = 'button';
    newButton.textContent = '+ New character';
    newButton.style.cssText =
      'padding:10px 12px;font-size:14px;background:#222230;color:inherit;border:1px solid #34344a;border-radius:6px;cursor:pointer;';
    newButton.addEventListener('click', () => {
      this.characterFlow?.goToCreate();
      this.renderCreate();
    });

    const graveyard = document.createElement('button');
    graveyard.type = 'button';
    graveyard.dataset.action = 'open-graveyard';
    graveyard.textContent = '⚰ Graveyard';
    graveyard.style.cssText =
      'padding:8px 12px;font-size:13px;background:transparent;color:inherit;border:1px solid #2a2a36;border-radius:6px;cursor:pointer;opacity:0.75;';
    graveyard.addEventListener('click', () => {
      void this.renderGraveyard();
    });

    wrap.append(title, subtitle, list, newButton, graveyard);
    this.root.appendChild(wrap);
  }

  /**
   * Graveyard / History view (HEX-65) — every character ever, alive + fallen,
   * with status, discoveries, gold, and (for the fallen) where + how they fell.
   * Reachable from the roster; Back returns to the active roster (or create when
   * none remain).
   */
  private async renderGraveyard(): Promise<void> {
    if (!this.persistence) return;
    const all = await this.persistence.listAllCharacters();

    this.root.innerHTML = '';
    const wrap = document.createElement('div');
    wrap.className = 'screen screen-graveyard';
    wrap.style.cssText =
      'flex:1;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:16px;padding:24px;';

    const title = document.createElement('h1');
    title.textContent = 'Graveyard';
    title.style.cssText = 'margin:0;font-size:28px;letter-spacing:0.12em;';

    const subtitle = document.createElement('p');
    subtitle.textContent =
      all.length === 0 ? 'No souls recorded yet.' : 'Every soul who walked these hexes.';
    subtitle.style.cssText = 'margin:0;opacity:0.75;';

    const list = document.createElement('ul');
    list.style.cssText =
      'list-style:none;margin:0;padding:0;display:flex;flex-direction:column;gap:8px;min-width:360px;max-height:60vh;overflow-y:auto;';
    // Fallen first, then alive; each group most-recent first (createdAt desc).
    const ordered = [...all].sort((a, b) => {
      const aDead = a.death ? 1 : 0;
      const bDead = b.death ? 1 : 0;
      if (aDead !== bDead) return bDead - aDead;
      return b.createdAt - a.createdAt;
    });
    for (const character of ordered) {
      list.appendChild(renderGraveyardRow(character));
    }

    const back = document.createElement('button');
    back.type = 'button';
    back.dataset.action = 'graveyard-back';
    back.textContent = 'Back';
    back.style.cssText =
      'padding:8px 12px;font-size:13px;background:#222230;color:inherit;border:1px solid #34344a;border-radius:6px;cursor:pointer;';
    back.addEventListener('click', async () => {
      if (!this.characterFlow) return;
      const screen = await this.characterFlow.init();
      await this.renderFlowScreen(screen);
    });

    wrap.append(title, subtitle, list, back);
    this.root.appendChild(wrap);
  }

  private renderRosterRow(character: Character): HTMLLIElement {
    const row = document.createElement('li');
    row.dataset.characterId = character.id;
    row.style.cssText =
      'display:flex;align-items:center;justify-content:space-between;gap:12px;padding:10px 12px;background:#15151b;border:1px solid #2a2a36;border-radius:6px;';

    const info = document.createElement('div');
    info.style.cssText = 'display:flex;flex-direction:column;gap:2px;';
    const name = document.createElement('span');
    name.textContent = character.name;
    name.style.cssText = 'font-size:15px;';
    const meta = document.createElement('span');
    meta.textContent = `(${character.position.q}, ${character.position.r})`;
    meta.style.cssText = 'font-size:11px;opacity:0.6;letter-spacing:0.05em;';
    info.append(name, meta, renderRosterDiscoveries(character));

    const actions = document.createElement('div');
    actions.style.cssText = 'display:flex;gap:6px;';

    const play = document.createElement('button');
    play.type = 'button';
    play.textContent = 'Continue';
    play.style.cssText =
      'padding:6px 10px;font-size:13px;background:#222230;color:inherit;border:1px solid #34344a;border-radius:6px;cursor:pointer;';
    play.addEventListener('click', async () => {
      const loaded = await this.characterFlow?.selectCharacter(character.id);
      if (loaded) await this.enterGame(loaded);
    });

    const retire = document.createElement('button');
    retire.type = 'button';
    retire.textContent = 'Retire';
    retire.dataset.action = 'retire';
    retire.style.cssText =
      'padding:6px 10px;font-size:13px;background:transparent;color:#e07070;border:1px solid #5a2424;border-radius:6px;cursor:pointer;';
    retire.addEventListener('click', async () => {
      if (!this.characterFlow) return;
      const next = await this.characterFlow.retire(character.id);
      await this.renderFlowScreen(next);
    });

    actions.append(play, retire);
    row.append(info, actions);
    return row;
  }

  // ─── game session ───────────────────────────────────────────────────────

  private async enterGame(character: Character): Promise<void> {
    if (!this.persistence) return;
    if (this.game) this.game.destroy();

    this.root.innerHTML = '';
    const wrap = document.createElement('div');
    wrap.className = 'screen screen-game';
    wrap.style.cssText = 'flex:1;display:flex;flex-direction:column;min-height:0;';

    const stage = document.createElement('div');
    stage.className = 'render-stage';
    stage.style.cssText = 'flex:1;position:relative;min-height:0;';

    const hud = document.createElement('div');
    hud.style.cssText =
      'position:absolute;top:8px;left:12px;font-size:12px;opacity:0.7;pointer-events:none;';
    hud.textContent = `Playing as ${character.name}. Click any revealed hex to walk there.`;
    stage.appendChild(hud);

    const exitBtn = document.createElement('button');
    exitBtn.type = 'button';
    exitBtn.textContent = 'Roster';
    exitBtn.style.cssText =
      'position:absolute;top:8px;right:12px;padding:6px 10px;font-size:12px;background:#222230;color:inherit;border:1px solid #34344a;border-radius:6px;cursor:pointer;z-index:5;';
    exitBtn.addEventListener('click', async () => {
      await this.exitGameToRoster();
    });
    stage.appendChild(exitBtn);

    const inventoryBtn = document.createElement('button');
    inventoryBtn.type = 'button';
    inventoryBtn.dataset.action = 'open-inventory';
    inventoryBtn.textContent = 'Inventory (I)';
    inventoryBtn.style.cssText =
      'position:absolute;top:8px;right:84px;padding:6px 10px;font-size:12px;background:#222230;color:inherit;border:1px solid #34344a;border-radius:6px;cursor:pointer;z-index:5;';
    stage.appendChild(inventoryBtn);

    wrap.appendChild(stage);
    this.root.appendChild(wrap);

    const game = new GameSession({
      character,
      persistence: this.persistence,
      container: stage,
    });
    inventoryBtn.addEventListener('click', () => {
      game.openInventory();
    });
    game.setCombatExitHandler(async (outcome) => {
      // Victory keeps the player in the world; defeat boots them back to the
      // roster (HEX-13 death pipe: the character has already been deleted by
      // `resolveCombatOutcome`, so the roster either shows survivors or
      // routes through to the create screen).
      if (outcome === 'defeat') {
        await this.exitGameToRoster();
      }
    });
    this.game = game;
    await game.start();
  }

  private async exitGameToRoster(): Promise<void> {
    if (!this.characterFlow) return;
    this.game?.destroy();
    this.game = null;
    const characters = await this.characterFlow.persistence.listCharacters();
    if (characters.length === 0) {
      this.characterFlow.goToCreate();
      this.renderCreate();
    } else {
      this.renderRoster(characters);
    }
  }

  /** Test seam: current screen kind without exposing internals. */
  getScreenKind(): string {
    const wrap = this.root.querySelector('.screen');
    return wrap?.className.replace('screen ', '') ?? 'none';
  }

  /** Test seam — exposes the underlying flow for tests once `mount` has completed. */
  getFlow(): CharacterFlow | null {
    return this.characterFlow;
  }

  /** Test seam — has the boot pipeline finished? */
  isBooted(): boolean {
    return this.booted;
  }
}

/** A graveyard row (HEX-65): name + status badge, discoveries/gold, and the
 * fall record for the dead. Pure DOM — no interaction (the dead don't play). */
function renderGraveyardRow(character: Character): HTMLLIElement {
  const dead = character.death != null;
  const row = document.createElement('li');
  row.dataset.characterId = character.id;
  row.dataset.status = dead ? 'fallen' : 'alive';
  row.style.cssText = `display:flex;flex-direction:column;gap:4px;padding:10px 12px;background:#15151b;border:1px solid ${
    dead ? '#3a2730' : '#2a2a36'
  };border-radius:6px;`;

  const head = document.createElement('div');
  head.style.cssText = 'display:flex;align-items:center;justify-content:space-between;gap:12px;';
  const name = document.createElement('span');
  name.textContent = character.name;
  name.style.cssText = 'font-size:15px;';
  const status = document.createElement('span');
  status.dataset.role = 'graveyard-status';
  status.textContent = dead ? '⚰ Fallen' : '● Alive';
  status.style.cssText = `font-size:11px;letter-spacing:0.06em;opacity:0.85;color:${
    dead ? '#e07070' : '#70c090'
  };`;
  head.append(name, status);

  const stats = document.createElement('span');
  stats.style.cssText = 'font-size:11px;opacity:0.6;letter-spacing:0.05em;';
  stats.textContent = `${character.discoveries} discoveries · ${character.gold} gold · Level ${character.level ?? 1}`;

  row.append(head, stats);

  if (character.death != null) {
    const death = character.death;
    const fell = document.createElement('span');
    fell.dataset.role = 'graveyard-death';
    fell.style.cssText = 'font-size:11px;opacity:0.6;font-style:italic;';
    const where = `(${death.hex.q}, ${death.hex.r})`;
    fell.textContent =
      death.cause === null ? `Fell at ${where}` : `Slain by ${death.cause} at ${where}`;
    row.appendChild(fell);
  }

  return row;
}

function defaultNewId(): CharacterId {
  // Crypto-grade UUID where available (modern browsers + Node ≥ 19).
  const cryptoRef = (globalThis as { crypto?: { randomUUID?: () => string } }).crypto;
  if (cryptoRef?.randomUUID) return cryptoRef.randomUUID();
  return `c-${Math.random().toString(36).slice(2, 10)}-${Date.now().toString(36)}`;
}

// ─── game session ────────────────────────────────────────────────────────

interface GameSessionOptions {
  readonly character: Character;
  readonly persistence: Persistence;
  readonly container: HTMLElement;
}

/**
 * Holds the wiring for a single live character: renderer + world store +
 * movement + vision + lifecycle + hover panel. Lifetime is bounded by
 * `start()` / `destroy()`.
 */
class GameSession {
  private readonly opts: GameSessionOptions;
  private renderer: Renderer | null = null;
  private worldStore: WorldStore | null = null;
  private vision: Vision | null = null;
  private hoverPanel: HoverPanel | null = null;
  /** CharacterId → name cache for hover presentation (HEX-71). Seeded from every
   * character ever — alive + fallen (HEX-65) — at session start; the live
   * character is always present. Best-effort: any id it still can't resolve falls
   * back in the hover content builder. */
  private readonly characterNames = new Map<string, string>();
  private lifecycle: TileLifecycle | null = null;
  private movement: Movement | null = null;
  /**
   * Counted per-world movement clock (HEX-99 / ADR-0018 §5): one player step =
   * one tick. Loaded from persistence at session start, advanced once per step in
   * `onMoved`. No consumer is wired yet — Evil Pressure / town reversion is Wave 3.
   */
  private worldClock: WorldClock | null = null;
  private discoveryHud: DiscoveryHud | null = null;
  private worldHud: WorldHud | null = null;
  private encounterFlow: EncounterFlow | null = null;
  private bossFlow: BossFlow | null = null;
  private chestFlow: ChestFlow | null = null;
  private shopFlow: ShopFlow | null = null;
  private townFlow: TownFlow | null = null;
  private merchantFlow: MerchantFlow | null = null;
  private eventFlow: EventFlow | null = null;
  private pillarFlow: PillarFlow | null = null;
  private inventoryEditor: InventoryEditor | null = null;
  private levelUpOverlay: LevelUpOverlay | null = null;
  private acquiring: AcquisitionSession | null = null;
  private compassHud: CompassHud | null = null;
  private activeOverlay:
    | 'combat'
    | 'boss'
    | 'chest'
    | 'shop'
    | 'town'
    | 'merchant'
    | 'event'
    | 'pillar'
    | 'inventory'
    | 'levelup'
    | null = null;
  /**
   * Liberated-town registry (HEX-122 / ADR-0018 §4), keyed by `hexKey`. Seeds the
   * `safeZoneDistance` easing: a liberated town softens danger within
   * `SETTLEMENT_EASE_RADIUS`. Seeded from the loaded world at session start and
   * extended on each in-session liberation.
   */
  private readonly liberatedTowns = new Map<string, { q: number; r: number }>();
  private onCombatExit:
    | ((outcome: 'victory' | 'defeat' | 'forfeit') => void | Promise<void>)
    | null = null;
  /** Every hex this character has ever had in vision — drives the memory set. */
  private readonly discovered = new Set<string>();
  private renderedHexes = new Set<string>();
  private currentVision: Set<string> = new Set();
  private clickUnsub: (() => void) | null = null;
  private hoverUnsub: (() => void) | null = null;
  /** Live worldview zoom factor (HEX-74); clamped via `clampZoom` (HEX-144). */
  private zoom = 1;
  /**
   * Free-look survey mode (HEX-96). While true the arrow keys pan the camera
   * independently of the character (renderer holds the offset); `f` toggles it,
   * `f` again or Escape recentres on the character. HEX-144 adds map-style
   * drag-to-pan (a real drag auto-enters this mode) and an on-screen cue.
   */
  private freeLook = false;
  /** Free-look mode banner (HEX-144) — advertises the survey mode + recentre. */
  private freeLookCue: FreeLookCue | null = null;
  /** Pointer drag-pan state (HEX-144). Active between pointerdown and -up over
   *  the stage; `moved` flips once travel passes `DRAG_PAN_THRESHOLD_PX`. */
  private dragPan: { lastX: number; lastY: number; moved: boolean } | null = null;
  /** Set when a drag-pan consumes the trailing tap so it doesn't walk (HEX-144).
   *  Reset on the next pointerdown so it can never eat a later genuine click. */
  private suppressNextClick = false;
  private pointerDownHandler: ((e: PointerEvent) => void) | null = null;
  private pointerMoveHandler: ((e: PointerEvent) => void) | null = null;
  private pointerUpHandler: ((e: PointerEvent) => void) | null = null;
  /** Pending shift-click waypoints (HEX-74) — ordered intermediate route stops.
   *  Always kept fully reachable: a waypoint is only appended if its leg from
   *  the previous endpoint pathfinds. A plain click clears them. */
  private readonly waypoints: Hex[] = [];
  /** Hex currently under the pointer (HEX-74), drives the tentative preview leg. */
  private hoverHex: Hex | null = null;
  private wheelHandler: ((e: WheelEvent) => void) | null = null;
  /** True while an auto-walk loop is stepping; gates new world clicks. */
  private walking = false;
  /** Pending inter-step pacing timer, cleared on destroy. */
  private walkTimer: ReturnType<typeof setTimeout> | null = null;
  private keyHandler: ((e: KeyboardEvent) => void) | null = null;
  private rafHandle: number | null = null;
  private lastFrameMs = 0;
  private destroyed = false;
  /**
   * Wall-clock stamp captured once when this session's world starts (HEX-94 /
   * ADR-0012 §2). The encounter-respawn same-session gate compares a tile's
   * `resolvedAt` against it: a kill from a previous session/character
   * (`resolvedAt < sessionBootAt`) is eligible to respawn; a same-session kill
   * is not, so a hex can't be farmed in one sitting. This is a comparison gate
   * only — never folded into the deterministic respawn roll.
   */
  private sessionBootAt = 0;

  constructor(opts: GameSessionOptions) {
    this.opts = opts;
  }

  setCombatExitHandler(
    handler: (outcome: 'victory' | 'defeat' | 'forfeit') => void | Promise<void>,
  ): void {
    this.onCombatExit = handler;
  }

  async start(): Promise<void> {
    // Stamp the session boot epoch up front (HEX-94): every encounter resolved
    // after this instant is "this session" and excluded from respawn.
    this.sessionBootAt = Date.now();
    const character = this.opts.character;
    const { width, height } = this.opts.container.getBoundingClientRect();
    const renderer = createPixiRenderer();
    await renderer.init({
      container: this.opts.container,
      layout: { size: HEX_SIZE, origin: { x: 0, y: 0 } },
      viewport: {
        width: Math.max(640, Math.floor(width)),
        height: Math.max(480, Math.floor(height)),
      },
    });
    if (this.destroyed) {
      renderer.destroy();
      return;
    }
    this.renderer = renderer;

    const gen = new ChunkedWorldGen();
    const store = new WorldStore(WORLD_SEED, this.opts.persistence, gen);
    this.worldStore = store;
    await store.ensureAround(character.position, RENDER_BUFFER_RADIUS);
    if (this.destroyed) {
      renderer.destroy();
      return;
    }

    const movement = new Movement(character.position, store);
    this.movement = movement;
    const vision = new Vision(movement, VISION_RADIUS);
    const lifecycle = new TileLifecycle({
      persistence: this.opts.persistence,
      renderer,
      now: () => Date.now(),
      // Same seed the WorldStore generates with, so the latent roll's safe-zone
      // metric eases near the very frontier settlements worldgen placed (HEX-93).
      worldSeed: WORLD_SEED,
      // Liberated towns ease the danger metric just like the origin (HEX-122).
      liberatedTowns: () => [...this.liberatedTowns.values()],
    });
    this.vision = vision;
    this.lifecycle = lifecycle;
    // Seed the liberated-town registry from the loaded world (spawn town + any
    // town already liberated in persistence) so easing is live from the first step.
    this.seedLiberatedTowns();

    // World clock (HEX-99 / ADR-0018 §5): restore the per-world movement tick so
    // it keeps counting from where the world left off. World-scoped — shared by
    // every character on this map — so it loads from persistence, not the
    // character. Advanced per step in `onMoved`; no consumer reads it yet (Wave 3).
    this.worldClock = await WorldClock.load(this.opts.persistence);
    if (this.destroyed) {
      renderer.destroy();
      return;
    }

    // Seed the CharacterId→name cache for hover presentation (HEX-71) from every
    // character ever — alive and fallen (HEX-65) — so a tile's `fallenCharacters`
    // ids resolve to real names (completing the SLAIN BY line). Then overlay the
    // live character so it's always resolvable.
    for (const c of await this.opts.persistence.listAllCharacters()) {
      this.characterNames.set(c.id, c.name);
    }
    this.characterNames.set(character.id, character.name);
    const hoverPanel = new HoverPanel(this.opts.container, {
      resolveName: (id) => this.characterNames.get(id) ?? null,
    });
    hoverPanel.mount();
    this.hoverPanel = hoverPanel;

    // Mount the discoveries HUD before the spawn cross so the very first
    // increment (if the spawn tile happens to be a never-crossed hex) is
    // reflected in the readout. `TileLifecycle.onCross` mutates `character`
    // in place, so a post-cross `setCount` lifts the new value.
    const discoveryHud = new DiscoveryHud(this.opts.container);
    discoveryHud.mount(character.discoveries);
    this.discoveryHud = discoveryHud;

    // World progression HUD (HEX-131): level + XP-to-next bar + gold, stacked
    // below the discoveries readout. `refreshWorldHud` lifts it from `character`
    // wherever level / XP / gold can move (movement, level-up, and each flow's
    // App-level `onResolved`).
    const worldHud = new WorldHud(this.opts.container);
    worldHud.mount({ xp: character.xp ?? 0, gold: character.gold });
    this.worldHud = worldHud;

    renderer.setCameraTarget(character.position);
    renderer.setCharacterPosition(character.position);
    this.renderTilesAround(character.position);
    // HEX-84: re-seed this character's personally-explored fog into the memory
    // set before the first visibility pass, so previously-walked tiles paint in
    // memory mode from session start (else only the live vision disk renders).
    await this.materialiseExplored(character);
    // HEX-73: re-seed any map-revealed tiles into the memory set and render them
    // before the first visibility pass, so a previously-bought map's reveal is
    // visible from session start (persisted across reloads).
    await this.materialiseRevealed(character);
    // HEX-128: re-seed every tile ever in this character's vision so glimpsed-but-
    // uncrossed tiles paint in memory mode (a remembered `?` when undiscovered)
    // from session start, instead of reverting to black. Persisted across reloads.
    await this.materialiseSeen(character);
    // HEX-124: reveal the +2 hex radius around every already-liberated town so
    // the map shows them as safe havens from session start (ADR-0018 §6).
    await this.seedAllLiberatedTownVisibility();
    // HEX-127: surface distant mountains as skyline silhouettes from the first
    // frame so the spawn neighbourhood already reads its orientation peaks.
    await this.renderLandmarksAround(character.position);
    this.refreshVision();

    // HEX-42: seed the spawn-town cluster as discovered for the spawning
    // character so the 5 origin cells render their real types from session
    // start. For the first character globally these become their first
    // discoveries; for later characters the cells are already discovered by
    // the founder and this is a no-op.
    for (const [q, r] of SPAWN_CLUSTER_CELLS) {
      const tile = store.getTileAt({ q, r });
      if (tile) await lifecycle.seedDiscovery(character, tile);
    }

    // First-cross of the spawn hex (origin). HEX-10 lifecycle expects this so
    // the spawn tile records the footfall and runs the latent-encounter
    // bookkeeping (skipped at the cluster, but defence-in-depth). Discovery
    // metadata is already populated by the seed loop above.
    const spawnTile = store.getTileAt(character.position);
    if (spawnTile) await lifecycle.onCross(character, spawnTile);
    discoveryHud.setCount(character.discoveries);

    // ─── renderCompass — HEX-124 ──────────────────────────────────────────
    // Mount the schematic compass needle (ADR-0018 §6) and point it at the
    // nearest unliberated town. Hidden immediately if the neighbourhood is
    // already cleared.
    const compassHud = new CompassHud(this.opts.container);
    compassHud.mount();
    this.compassHud = compassHud;
    this.refreshCompass(character);
    // ─────────────────────────────────────────────────────────────────────

    // Free-look cue (HEX-144) — constructed hidden; shown while surveying.
    this.freeLookCue = new FreeLookCue(this.opts.container);

    const clickHandler = makeClickToMove(movement, (result, coord) => {
      if (!result.ok) return;
      void this.onMoved(character, coord);
    });
    this.clickUnsub = renderer.onHexClick((coord) => {
      // World input pauses while a non-world overlay (combat) owns the screen.
      // Belt-and-braces — the overlay's full-viewport DOM normally swallows the
      // pointer event before Pixi sees it.
      if (this.activeOverlay !== null) return;
      // A map drag-pan (HEX-144) ends with a trailing tap Pixi still fires —
      // swallow it once so surveying never walks the character to the release
      // hex.
      if (this.suppressNextClick) {
        this.suppressNextClick = false;
        return;
      }
      // Ignore fresh clicks while an auto-walk is in flight — the player waits
      // for the current path to finish (or be interrupted) before re-issuing.
      if (this.walking) return;
      const target: Hex = { q: coord.q, r: coord.r };
      // Shift-click appends a route waypoint (HEX-74) — it never walks.
      if (coord.shiftKey) {
        this.addWaypoint(target);
        return;
      }
      const from = movement.position;
      // Plain click commits — and always clears any pending waypoints, whether
      // or not the committed route is reachable (commit/replace semantics).
      if (this.waypoints.length > 0) {
        const endpoints = [...this.waypoints, target];
        this.clearWaypoints();
        const path = findPathThrough(from, endpoints, (hex) => this.isPathWalkable(hex));
        if (path && path.length >= 2) void this.walkPath(character, movement, path);
        return;
      }
      this.clearPreview();
      const d = distance(from, target);
      if (d === 0) return;
      // Adjacent single-step click: unchanged direct-move route (HEX-70 DoD).
      if (d === 1) {
        clickHandler(target);
        return;
      }
      // Distant click: pathfind over known/revealed passable tiles (HEX-70).
      // Unreachable or unrevealed targets yield no path → silent no-op.
      const path = findPath(from, target, (hex) => this.isPathWalkable(hex));
      if (!path || path.length < 2) return;
      void this.walkPath(character, movement, path);
    });

    this.hoverUnsub = renderer.onHexHover((event) => {
      this.onHover(event);
    });

    this.keyHandler = (e: KeyboardEvent) => this.onKey(e);
    window.addEventListener('keydown', this.keyHandler);

    // Scroll-wheel drives the character-centred zoom (HEX-74); `passive: false`
    // so we can preventDefault and stop the page from scrolling under us.
    this.wheelHandler = (e: WheelEvent) => {
      if (this.activeOverlay !== null) return;
      e.preventDefault();
      this.zoomBy(e.deltaY < 0 ? ZOOM_STEP : 1 / ZOOM_STEP);
    };
    this.opts.container.addEventListener('wheel', this.wheelHandler, { passive: false });

    // Map-style drag-to-pan (HEX-144). A press-and-drag over the stage surveys
    // the world without moving the character: a real drag (travel past the
    // threshold) auto-enters free-look and shifts the camera by the pointer
    // delta; the trailing tap is suppressed so the release never walks. A plain
    // tap is untouched and still walks. Pointer-capture keeps the drag alive if
    // the pointer briefly leaves the surface.
    this.pointerDownHandler = (e: PointerEvent) => {
      if (this.activeOverlay !== null) return;
      if (e.button !== 0) return;
      this.suppressNextClick = false;
      this.dragPan = { lastX: e.clientX, lastY: e.clientY, moved: false };
    };
    this.pointerMoveHandler = (e: PointerEvent) => {
      const drag = this.dragPan;
      if (!drag) return;
      const dx = e.clientX - drag.lastX;
      const dy = e.clientY - drag.lastY;
      if (!drag.moved) {
        if (Math.hypot(e.clientX - drag.lastX, e.clientY - drag.lastY) < DRAG_PAN_THRESHOLD_PX) {
          return;
        }
        drag.moved = true;
        this.setFreeLook(true);
        try {
          this.opts.container.setPointerCapture(e.pointerId);
        } catch {
          // setPointerCapture can throw if the pointer is already gone; the pan
          // still works without capture, so ignore.
        }
      }
      drag.lastX = e.clientX;
      drag.lastY = e.clientY;
      this.renderer?.panBy(dx, dy);
    };
    this.pointerUpHandler = (e: PointerEvent) => {
      const drag = this.dragPan;
      this.dragPan = null;
      if (!drag) return;
      if (drag.moved) this.suppressNextClick = true;
      try {
        this.opts.container.releasePointerCapture(e.pointerId);
      } catch {
        // No capture held — nothing to release.
      }
    };
    this.opts.container.addEventListener('pointerdown', this.pointerDownHandler);
    this.opts.container.addEventListener('pointermove', this.pointerMoveHandler);
    this.opts.container.addEventListener('pointerup', this.pointerUpHandler);
    this.opts.container.addEventListener('pointercancel', this.pointerUpHandler);

    const tick = (now: number) => {
      if (this.destroyed) return;
      const delta = this.lastFrameMs === 0 ? 0 : now - this.lastFrameMs;
      this.lastFrameMs = now;
      this.renderer?.frame(delta);
      this.rafHandle = requestAnimationFrame(tick);
    };
    this.rafHandle = requestAnimationFrame(tick);
  }

  private async onMoved(character: Character, target: Hex): Promise<void> {
    const store = this.worldStore;
    const renderer = this.renderer;
    const lifecycle = this.lifecycle;
    if (!store || !renderer || !lifecycle) return;
    await store.ensureAround(target, RENDER_BUFFER_RADIUS);
    if (this.destroyed) return;
    this.renderTilesAround(target);
    // Movement smooth-pans the camera (ADR-0014 §4) AND tweens the marker
    // (ADR-0014 §3 moment 2); teleport intents request 'snap' explicitly.
    renderer.setCameraTarget(target, 'smooth');
    renderer.setCharacterPosition(target, { mode: 'smooth' });
    // HEX-127: refresh the skyline as the character steps — new peaks rise into
    // landmark range, passed ones drop off behind.
    await this.renderLandmarksAround(target);
    if (this.destroyed) return;
    this.refreshVision();

    const tile = store.getTileAt(target);
    if (tile) await lifecycle.onCross(character, tile);
    this.discoveryHud?.setCount(character.discoveries);
    // Tile-cross XP (HEX-117): award on a newly-crossed hex only (per-character
    // explored set — no double-count on re-cross). Checked BEFORE adding the hex
    // to explored so the predicate is accurate.
    const isNewHex = !character.explored.has(hexKey(target));
    // Persist character position + explored set on each move.
    character.position = target;
    character.explored.add(hexKey(target));
    let crossLeveledUp = false;
    if (isNewHex) {
      // A single tile-cross (+XP_TILE_CROSS) can never span two levels, so a
      // boolean suffices here — only the combat path needs the per-level loop.
      crossLeveledUp = addXp(character, XP_TILE_CROSS).levelsGained > 0;
      if (crossLeveledUp) character.hp = PLAYER_MAX_HP;
    }
    await this.opts.persistence.saveCharacter(character);
    if (crossLeveledUp) {
      await this.runLevelUpOverlay(character, target);
    }
    // HEX-131: reflect tile-cross XP (and any level-up) in the world HUD.
    this.refreshWorldHud(character);

    // HEX-124: repoint compass on every move (ADR-0018 §6).
    this.refreshCompass(character);

    // World clock (HEX-99 / ADR-0018 §5): one step = one tick. Advance on EVERY
    // movement — including a re-cross of an already-discovered tile — because the
    // world's unit of time is *movement*, not discovery; `onMoved` runs once per
    // committed step (single click and each auto-walk hop alike). Pillar
    // fast-travel deliberately does NOT tick here: whether a hop costs many ticks
    // at once is an open ADR-0019 (§7) question, deferred.
    await this.worldClock?.advance();

    // Evil Pressure lazy evaluation (HEX-123 / ADR-0018 §5): when the player
    // steps onto a town tile, recompute Stability for that town and persist any
    // state change (liberated → under-threat → evil). Must run BEFORE the
    // overlay dispatch so maybeStartBossCombat sees the updated townState.
    if (tile?.type === 'town') await this.onTownApproach(tile);

    // Encounter / chest / shop tiles pre-empt the walk loop: spin up the
    // relevant overlay, pause world input, and route intents through it
    // until it closes.
    if (!tile) return;
    if (this.maybeStartCombat(character, tile)) return;
    if (this.maybeStartEvent(character, tile)) return;
    if (this.maybeStartPillar(character, tile)) return;
    if (this.maybeStartChest(character, tile)) return;
    if (await this.maybeStartShop(character, tile)) return;
    // Boss-combat pre-empts the town modal (HEX-122 / ADR-0018 §4): an evil town
    // must be liberated before its heal/rest services open.
    if (this.maybeStartBossCombat(character, tile)) return;
    if (this.maybeStartTown(character, tile)) return;
    this.maybeStartMerchant(character, tile);
  }

  /**
   * Pathfinding passability (HEX-70). A hex is walkable for auto-walk only if
   * the character has it in their revealed/known space (`discovered`) AND its
   * materialised tile is terrain-passable. Bounding to `discovered` keeps the
   * search over known space — we never route through unrevealed frontier.
   */
  private isPathWalkable(hex: Hex): boolean {
    const store = this.worldStore;
    if (!store) return false;
    if (!this.discovered.has(hexKey(hex))) return false;
    const tile = store.getTileAt(hex);
    return !!tile && isPassable(tile.type);
  }

  /**
   * Auto-walk a pre-computed path (HEX-70). Steps one hex at a time through the
   * normal `onMoved` flow, so a path of N hops is identical to N individual
   * adjacent clicks — same discovery / vision / persist + encounter triggering.
   *
   * If any step opens an overlay (combat / chest / shop), the walk STOPS and
   * hands control to that overlay. We do NOT auto-resume afterwards — the
   * player re-issues the click. (Auto-resume is a possible later nicety.)
   *
   * `path[0]` is the current position; walking begins at `path[1]`.
   */
  private async walkPath(character: Character, movement: Movement, path: Hex[]): Promise<void> {
    if (this.walking) return;
    this.walking = true;
    try {
      for (let i = 1; i < path.length; i++) {
        if (this.destroyed) return;
        // An overlay opened (or the world was torn down) since the last step —
        // stop rather than walk blindly on.
        if (this.activeOverlay !== null) return;
        const step = path[i] as Hex;
        const result = movement.tryMove(step);
        // A tile that was walkable at plan time is no longer reachable (e.g. a
        // chunk eviction dropped it). Stop gracefully.
        if (!result.ok) return;
        await this.onMoved(character, step);
        if (this.destroyed) return;
        // onMoved spins up the overlay synchronously when the tile is an
        // encounter / chest / shop — pause the walk and let it take over.
        if (this.activeOverlay !== null) return;
        // Pace the remaining hops so the smooth camera/marker tween reads as a
        // walk. The final hop needs no trailing delay.
        if (i < path.length - 1) await this.walkDelay(AUTO_WALK_STEP_MS);
      }
    } finally {
      this.walking = false;
    }
  }

  /** Inter-step pacing timer; resolves after `ms`, tracked so `destroy` cancels it. */
  private walkDelay(ms: number): Promise<void> {
    return new Promise((resolve) => {
      this.walkTimer = setTimeout(() => {
        this.walkTimer = null;
        resolve();
      }, ms);
    });
  }

  private maybeStartCombat(character: Character, tile: Tile): boolean {
    if (this.activeOverlay !== null) return false;
    const flow = this.encounterFlow ?? new EncounterFlow(this.opts.container);
    this.encounterFlow = flow;
    const started = flow.start({
      character,
      tile,
      worldSeed: WORLD_SEED,
      // HEX-94: the boot stamp gates encounter respawn (a previous-session kill
      // can come back to life on this read; a same-session kill cannot).
      sessionBootAt: this.sessionBootAt,
      deps: { persistence: this.opts.persistence },
      onResolved: async (outcome) => {
        this.activeOverlay = null;
        // Victory clears the encounter — repaint so the death-mark glyph
        // (HEX-71) replaces the live ⚔ immediately, not on the next reveal.
        if (outcome === 'victory') {
          this.renderer?.updateHex({ q: tile.q, r: tile.r }, tile);
        }
        await this.onCombatExit?.(outcome);
        // HEX-131: combat reward (gold + XP) is applied to `character` by the
        // overlay before this resolves — lift it into the HUD.
        this.refreshWorldHud(character);
      },
    });
    if (started) {
      this.activeOverlay = 'combat';
      // A respawn at `start` may have flipped a cleared remnant back to a live
      // encounter — repaint so the `†` death-mark gives way to the live ⚔ even
      // if the player forfeits before the fight resolves (HEX-94).
      this.renderer?.updateHex({ q: tile.q, r: tile.r }, tile);
    }
    return started;
  }

  private maybeStartChest(character: Character, tile: Tile): boolean {
    if (this.activeOverlay !== null) return false;
    const flow = this.chestFlow ?? new ChestFlow(this.opts.container);
    this.chestFlow = flow;
    const started = flow.start({
      character,
      tile,
      deps: {
        persistence: this.opts.persistence,
        rng: createRng(chestSeed(tile.q, tile.r, tile.crossings)),
        // Frontier settlements ease the distance tier scaling the gold (HEX-93).
        worldSeed: WORLD_SEED,
      },
      onResolved: async () => {
        this.activeOverlay = null;
        // HEX-131: chest reward (gold / card-or-gold) → HUD.
        this.refreshWorldHud(character);
      },
    });
    if (started) this.activeOverlay = 'chest';
    return started;
  }

  /** Danger-tile chance event (HEX-137 / ADR-0023): fire one event from the
   * weighted table — gamble / ambush / cache / quiet — seeded per (tile, cross).
   * An ambush defeat (or any death) routes through the death pipe (defeat →
   * exit to roster), mirroring combat defeat. */
  private maybeStartEvent(character: Character, tile: Tile): boolean {
    if (this.activeOverlay !== null) return false;
    const flow = this.eventFlow ?? new EventFlow(this.opts.container);
    this.eventFlow = flow;
    const started = flow.start({
      character,
      tile,
      deps: {
        persistence: this.opts.persistence,
        now: () => Date.now(),
        rng: createRng(eventSeed(tile.q, tile.r, tile.crossings)),
        // Frontier settlements ease the danger tier near a frontier town (HEX-93).
        worldSeed: WORLD_SEED,
      },
      onResolved: async (outcome) => {
        this.activeOverlay = null;
        if (outcome === 'defeat') await this.onCombatExit?.('defeat');
        // HEX-131: chance-event gold (gamble / cache / ambush drop) → HUD.
        this.refreshWorldHud(character);
      },
    });
    if (started) this.activeOverlay = 'event';
    return started;
  }

  /**
   * Teleport-pillar fast travel (HEX-92; ADR-0004 primitive). Stepping
   * onto a pillar opens a modal listing the OTHER pillars this character knows —
   * `explored` ∩ pillar tiles (no new registry; the host derives the set). The
   * flow charges gold + moves `character.position`, then we relocate the live
   * session WITHOUT a reboot — snap the camera + marker to the destination
   * (renderer 'snap'), re-seed vision, and run the destination tile's lifecycle
   * cross.
   */
  private maybeStartPillar(character: Character, tile: Tile): boolean {
    if (this.activeOverlay !== null) return false;
    if (tile.type !== 'pillar') return false;
    const flow = this.pillarFlow ?? new PillarFlow(this.opts.container);
    this.pillarFlow = flow;
    const started = flow.start({
      character,
      tile,
      destinations: this.knownPillarDestinations(character, tile),
      deps: { persistence: this.opts.persistence },
      onResolved: async (outcome, destination) => {
        this.activeOverlay = null;
        if (outcome === 'travelled' && destination) {
          await this.arriveByPillar(character, destination);
        }
      },
    });
    if (started) this.activeOverlay = 'pillar';
    return started;
  }

  /**
   * Other pillars this character can fast-travel to: `Character.explored` ∩
   * pillar tiles in the world, with the pillar currently stood on excluded. Pure
   * read over the materialised world store — no separate pillar registry (DoD).
   * Explored hexes whose chunk isn't loaded read `null` and are skipped; the
   * spawn-cluster pillar (0, 1) lands in `explored` on first cross and persists.
   */
  private knownPillarDestinations(character: Character, current: Tile): Hex[] {
    const store = this.worldStore;
    if (!store) return [];
    const out: Hex[] = [];
    for (const key of character.explored) {
      const hex = parseHexKey(key);
      if (!hex) continue;
      if (hex.q === current.q && hex.r === current.r) continue; // exclude this pillar
      const tile = store.getTileAt(hex);
      if (tile && tile.type === 'pillar') out.push(hex);
    }
    return out;
  }

  /**
   * Relocate the live session to a pillar-travel destination (HEX-92). The flow
   * has already charged gold + rewritten `character.position` + persisted; here we
   * move the world side: warm the destination's chunks, teleport the movement
   * model (the FoV source), repaint + snap the camera/marker with the teleport
   * discontinuity preserved ('snap'), re-seed vision, and cross the destination
   * tile so its lifecycle (discovery credit etc.) fires exactly like a walk.
   */
  private async arriveByPillar(character: Character, destination: Hex): Promise<void> {
    const store = this.worldStore;
    const renderer = this.renderer;
    const lifecycle = this.lifecycle;
    const movement = this.movement;
    if (!store || !renderer || !lifecycle || !movement) return;
    await store.ensureAround(destination, RENDER_BUFFER_RADIUS);
    if (this.destroyed) return;
    movement.teleportTo(destination);
    this.renderTilesAround(destination);
    // Teleport is a discontinuity — snap, don't pan (renderer 'snap', ADR-0014 §4).
    renderer.setCameraTarget(destination, 'snap');
    renderer.setCharacterPosition(destination, { mode: 'snap' });
    // HEX-127: paint the arrival neighbourhood's skyline before the vision pass.
    await this.renderLandmarksAround(destination);
    if (this.destroyed) return;
    this.refreshVision();

    const tile = store.getTileAt(destination);
    if (tile) await lifecycle.onCross(character, tile);
    this.discoveryHud?.setCount(character.discoveries);
    // HEX-131: pillar fast-travel charges gold in the flow → HUD.
    this.refreshWorldHud(character);
    character.explored.add(hexKey(destination));
    await this.opts.persistence.saveCharacter(character);
  }

  /**
   * Open the out-of-combat inventory editor over the world (ADR-0015 §12 —
   * editing is a worldview screen). Pauses world input like the other overlays;
   * edits write back to the persisted character on each change and on close.
   * No-op while another overlay (combat / chest / shop / editor) owns the screen.
   */
  openInventory(): void {
    if (this.activeOverlay !== null) return;
    const character = this.opts.character;
    const editor = new InventoryEditor(character.inventory);
    this.inventoryEditor = editor;
    this.activeOverlay = 'inventory';
    editor.mount(this.opts.container, {
      inventory: character.inventory,
      onChange: (inv) => {
        character.inventory = inv;
        void this.opts.persistence.saveCharacter(character);
      },
      onClose: () => {
        editor.destroy();
        this.inventoryEditor = null;
        this.activeOverlay = null;
        void this.opts.persistence.saveCharacter(character);
      },
    });
  }

  /**
   * Show the level-up reward overlay (HEX-117). Blocks the world loop until
   * the player dismisses. Seed mixes the target hex coords and the new level
   * so different level-ups at different hexes produce distinct offer sets.
   */
  private runLevelUpOverlay(character: Character, hex: Hex): Promise<void> {
    return new Promise((resolve) => {
      const seed = ((hex.q * 7919) ^ (hex.r * 65537) ^ ((character.level ?? 1) * 999983)) | 0;
      const offers = rollLevelUpOffers(createRng(seed));
      const overlay = new LevelUpOverlay(this.opts.container);
      this.levelUpOverlay = overlay;
      this.activeOverlay = 'levelup';
      const finish = () => {
        overlay.destroy();
        this.levelUpOverlay = null;
        this.activeOverlay = null;
        resolve();
      };
      overlay.start({
        level: character.level ?? 1,
        offers,
        onPick: async (offer) => {
          // Route the chosen card/die through the shared acquisition drawer (offer
          // beside the loadout, curate-and-place — HEX-149) so a full grid no
          // longer silently declines the level-up reward.
          const item = offer.kind === 'card' ? offer.card : dieCard(offer.die);
          this.acquiring = openAcquisition(this.opts.container, character, {
            item,
            declineLabel: 'Forgo',
          });
          await this.acquiring.done;
          this.acquiring = null;
          await this.opts.persistence.saveCharacter(character);
        },
        onDone: finish,
      });
    });
  }

  private async maybeStartShop(character: Character, tile: Tile): Promise<boolean> {
    if (this.activeOverlay !== null) return false;
    // Guard on type before the reveal-radius warm pass so non-shop moves stay
    // cheap (the flow re-checks the type too).
    if (tile.type !== 'shop') return false;
    const flow = this.shopFlow ?? new ShopFlow(this.opts.container);
    this.shopFlow = flow;
    const mapOffer = await this.buildMapOffer(character, tile);
    const started = flow.start({
      character,
      tile,
      deps: { persistence: this.opts.persistence },
      mapOffer,
      onResolved: async () => {
        this.activeOverlay = null;
        // HEX-131: shop purchases spend gold → HUD.
        this.refreshWorldHud(character);
      },
    });
    if (started) this.activeOverlay = 'shop';
    return started;
  }

  /**
   * Build the HEX-73 map offer for a shop visit. Warms the metadata overlay for
   * the whole reveal radius (so `discoveredBy` is known beyond the render
   * buffer), then counts the tiles SOMEONE discovered that this character can't
   * yet see. `buy()` owns the whole transaction — gold, reveal, persist.
   */
  private async buildMapOffer(character: Character, shopTile: Tile): Promise<MapOffer> {
    const store = this.worldStore;
    const centre: Hex = { q: shopTile.q, r: shopTile.r };
    if (store) await store.ensureAround(centre, MAP_REVEAL_RADIUS);
    const keys =
      store === null
        ? []
        : revealableHexes(centre, MAP_REVEAL_RADIUS, {
            discoveredByAnyone: (hex) => {
              const t = store.getTileAt(hex);
              return t !== null && t.metadata.discoveredBy !== null;
            },
            // Already-visible-to-me: in this session's memory/vision set, or
            // personally explored, or already map-revealed. None are "hidden".
            knownToCharacter: (key) =>
              this.discovered.has(key) ||
              character.explored.has(key) ||
              character.revealed.has(key),
          });
    return {
      price: MAP_ITEM_PRICE,
      revealCount: keys.length,
      buy: async () => {
        if (character.gold < MAP_ITEM_PRICE) return;
        character.gold -= MAP_ITEM_PRICE;
        await this.applyMapReveal(character, keys);
      },
    };
  }

  /**
   * Apply a map purchase (HEX-73): fold the revealed keys into the character's
   * persisted `revealed` set and this session's memory set, render the
   * newly-revealed tiles (they may sit beyond the render buffer), repaint, and
   * persist. REVEAL-NOT-CREDIT: nothing here touches `explored`, `discoveries`,
   * or any tile's `discoveredBy` — the tiles render as inherited memory only.
   */
  private async applyMapReveal(character: Character, keys: readonly string[]): Promise<void> {
    const store = this.worldStore;
    const renderer = this.renderer;
    for (const key of keys) {
      character.revealed.add(key);
      this.discovered.add(key);
      if (!store || !renderer || this.renderedHexes.has(key)) continue;
      const hex = parseHexKey(key);
      const tile = hex ? store.getTileAt(hex) : null;
      if (hex && tile) {
        renderer.addHex(hex, tile);
        this.renderedHexes.add(key);
      }
    }
    this.refreshVision();
    await this.opts.persistence.saveCharacter(character);
  }

  /** Build the seed target binding the live store + renderer to the session's
   * memory + rendered sets (HEX-84 / HEX-73 boot seeds share this). */
  private seedTarget(store: WorldStore, renderer: Renderer): MemorySeedTarget {
    return {
      ensureChunk: (hex) => store.ensureAround(hex, 0),
      getTileAt: (hex) => store.getTileAt(hex),
      addHex: (hex, tile) => renderer.addHex(hex, tile),
      discovered: this.discovered,
      rendered: this.renderedHexes,
      isDestroyed: () => this.destroyed,
    };
  }

  /**
   * Re-seed a character's personally-explored fog into the memory set at session
   * start (HEX-84). Without this, `this.discovered` boots empty and only fills
   * from the live vision disk, so every previously-walked tile drops on refresh —
   * only the current vision disk would render. Mirrors `materialiseRevealed`; the
   * shared `seedMemory` kernel warms each chunk, records the key, and renders the
   * node so `refreshVision` paints it in the memory face. No discovery credit.
   */
  private async materialiseExplored(character: Character): Promise<void> {
    const store = this.worldStore;
    const renderer = this.renderer;
    if (!store || !renderer) return;
    await seedMemory(character.explored, this.seedTarget(store, renderer));
  }

  /**
   * Re-render a character's persisted map-revealed tiles at session start
   * (HEX-73). Each revealed hex may lie far from spawn, so warm its chunk, seed
   * it into the memory set, and add its node — `refreshVision` then paints it in
   * the inherited-memory face. No discovery credit is implied.
   */
  private async materialiseRevealed(character: Character): Promise<void> {
    const store = this.worldStore;
    const renderer = this.renderer;
    if (!store || !renderer) return;
    await seedMemory(character.revealed, this.seedTarget(store, renderer));
  }

  /**
   * Re-render a character's persisted "seen" fog at session start (HEX-128). Each
   * seen hex (ever in vision, crossed or not) may lie beyond the live disk, so
   * warm its chunk, seed it into the memory set, and add its node — `refreshVision`
   * then paints it in memory mode (a remembered `?` when still undiscovered, the
   * real face otherwise). REVEAL-NOT-CREDIT: no discovery credit is implied;
   * mirrors `materialiseExplored` / `materialiseRevealed`.
   */
  private async materialiseSeen(character: Character): Promise<void> {
    const store = this.worldStore;
    const renderer = this.renderer;
    if (!store || !renderer) return;
    await seedMemory(character.seen, this.seedTarget(store, renderer));
  }

  /**
   * Town-boss combat (HEX-122 / ADR-0018 §4). An `evil` town dispatches a scaled
   * boss fight that gates liberation; `liberated` towns fall through to the town
   * modal, `under-threat` is an inert stub (no boss, no services). Victory
   * registers the town for safe-zone easing and materialises its pillar; the
   * `liberateTown` call inside the flow repaints both tiles. Defeat routes to the
   * roster via the shared combat-exit handler.
   */
  private maybeStartBossCombat(character: Character, tile: Tile): boolean {
    if (this.activeOverlay !== null) return false;
    if (tile.type !== 'town') return false;
    if (getTownState(tile) !== 'evil') return false;
    const store = this.worldStore;
    if (!store) return false;
    const state = tile.lifecycleState;
    if (state === null || state.kind !== 'town') return false;
    const pillarHex = state.anchorTiles.pillar;
    const pillarSlotTile = store.getTileAt(pillarHex);
    // The reserved slot is a same-cluster neighbour, loaded by the move's
    // ensureAround — but guard rather than fight a boss we can't finish liberating.
    if (!pillarSlotTile) return false;
    const flow = this.bossFlow ?? new BossFlow(this.opts.container);
    this.bossFlow = flow;
    const deps = this.renderer
      ? { persistence: this.opts.persistence, renderer: this.renderer }
      : { persistence: this.opts.persistence };
    // Accumulated evil: how much harder the boss is from festering (HEX-123).
    const worldState = this.buildPressureWorldState();
    const localPressure = computePressure(tile, worldState);
    const extraTierOffset = Math.floor(accumulatedEvil(tile, worldState.worldTick, localPressure));
    const started = flow.start({
      character,
      townTile: tile,
      pillarSlotTile,
      worldSeed: WORLD_SEED,
      deps,
      extraTierOffset,
      onResolved: async (outcome) => {
        this.activeOverlay = null;
        if (outcome === 'victory') {
          this.registerLiberatedTown(tile);
          // Reset stability so the re-liberated town starts at S_MAX,
          // not the 0 from when it last fell (HEX-123 / ADR-0018 §5).
          await this.resetTownStability(tile);
          // HEX-124: reveal the +2 radius around the newly liberated town and
          // repoint the compass now that this town is no longer a target.
          await this.seedTownVisibilityRadius(tile.q, tile.r);
          this.refreshCompass(character);
        }
        await this.onCombatExit?.(outcome);
        // HEX-131: boss reward (gold + XP) → HUD.
        this.refreshWorldHud(character);
      },
    });
    if (started) this.activeOverlay = 'boss';
    return started;
  }

  /** Scan the loaded world for towns already liberated (spawn town + any
   * persisted liberation) and register them for safe-zone easing. */
  private seedLiberatedTowns(): void {
    const store = this.worldStore;
    if (!store) return;
    for (const tile of store.cachedTiles()) {
      if (tile.type === 'town' && getTownState(tile) === 'liberated') {
        this.registerLiberatedTown(tile);
      }
    }
  }

  private registerLiberatedTown(tile: Tile): void {
    this.liberatedTowns.set(hexKey({ q: tile.q, r: tile.r }), { q: tile.q, r: tile.r });
  }

  /**
   * Reveal the +2 hex radius around a liberated town into the session memory
   * set (HEX-124 / ADR-0018 §6). Reveal-not-credit: tiles become visible on
   * the map without touching `Character.explored`, `discoveries`, or
   * `discoveredBy` — identical seam to HEX-73's map item.
   *
   * Reverted / fallen towns do NOT call this — their radius is already in the
   * memory set from when they were liberated and stays there (the tile remains
   * visible per ADR-0018 §6 "still visible on map, but NO +2 radius" means we
   * don't *add* new radius tiles, not that we remove the existing ones).
   */
  private async seedTownVisibilityRadius(q: number, r: number): Promise<void> {
    const store = this.worldStore;
    const renderer = this.renderer;
    if (!store || !renderer) return;
    const hexes = hexesWithinRadius(q, r, TOWN_VISIBILITY_RADIUS);
    const keys = new Set<string>(hexes.map((h) => hexKey(h)));
    await seedMemory(keys, this.seedTarget(store, renderer));
  }

  /** Seed +2 visibility for every currently-liberated town (called at session
   *  start after `seedLiberatedTowns`). */
  private async seedAllLiberatedTownVisibility(): Promise<void> {
    for (const town of this.liberatedTowns.values()) {
      await this.seedTownVisibilityRadius(town.q, town.r);
      if (this.destroyed) return;
    }
  }

  /**
   * Repoint the compass needle (HEX-124). Scans the settlement lattice from the
   * character's position; passes the world store as the tile-lookup so loaded
   * tile states are respected. Hidden when all towns in search range are
   * liberated.
   */
  /** Lift the world HUD (level / XP bar / gold) from the live character. Called
   *  wherever XP or gold can move; cheap no-op when nothing changed (HEX-131). */
  private refreshWorldHud(character: Character): void {
    this.worldHud?.update({ xp: character.xp ?? 0, gold: character.gold });
  }

  private refreshCompass(character: Character): void {
    const compass = this.compassHud;
    const store = this.worldStore;
    if (!compass) return;
    const target = store
      ? nearestUnliberatedTown(character.position.q, character.position.r, WORLD_SEED, (hex) =>
          store.getTileAt(hex),
        )
      : null;
    compass.update(target, character.position.q, character.position.r);
  }

  // ─── pressure hook (HEX-123 / ADR-0018 §5) ──────────────────────────────

  /**
   * Build a read-only snapshot of world state for Evil Pressure calculations.
   * Uses the live world store + world clock; returns a no-op stub when either
   * is absent (before session boot or after destroy).
   */
  private buildPressureWorldState(): EvilPressureWorldState {
    const store = this.worldStore;
    return {
      worldSeed: WORLD_SEED,
      worldTick: this.worldClock?.tick ?? 0,
      getTownState: (q, r) => {
        if (!store) return null;
        const t = store.getTileAt({ q, r });
        if (!t || t.type !== 'town') return null;
        return getTownState(t);
      },
    };
  }

  /**
   * Lazily evaluate Evil Pressure / Stability for a town the player just stepped
   * on. Mutates the tile's lifecycle blob in memory and persists if the state
   * changed. Must be called BEFORE the overlay dispatch so maybeStartBossCombat
   * sees the updated townState.
   *
   * Spawn town is immune (evaluateTownStability returns null); evil towns skip
   * (no drain needed — already fallen).
   */
  private async onTownApproach(tile: Tile): Promise<void> {
    if (tile.type !== 'town') return;
    const worldState = this.buildPressureWorldState();
    const update = evaluateTownStability(tile, worldState);
    if (update === null) return;

    const state = tile.lifecycleState as TownLifecycleState | null;
    if (state === null || state.kind !== 'town') return;

    tile.lifecycleState = {
      ...state,
      townState: update.townState,
      stability: update.stability,
      stabilityLastComputedAt: worldState.worldTick,
      ...(update.tickFallen !== undefined ? { tickFallen: update.tickFallen } : {}),
    };
    await this.opts.persistence.saveTileMutations(tile);
  }

  /**
   * Reset stability fields after (re-)liberation so the town starts fresh at
   * STABILITY_MAX rather than inheriting the 0 from its last fall.
   * Called from maybeStartBossCombat's onResolved on victory.
   */
  private async resetTownStability(tile: Tile): Promise<void> {
    const state = tile.lifecycleState as TownLifecycleState | null;
    if (!state || state.kind !== 'town') return;
    // Omit tickFallen so the key is fully absent (exactOptionalPropertyTypes).
    const { tickFallen: _drop, ...rest } = state;
    tile.lifecycleState = {
      ...rest,
      stability: STABILITY_MAX,
      stabilityLastComputedAt: this.worldClock?.tick ?? 0,
    };
    await this.opts.persistence.saveTileMutations(tile);
  }

  /** Town heal (HEX-72): spend gold for HP at the settlement tile. Shops no
   * longer heal — this is the only heal source. */
  private maybeStartTown(character: Character, tile: Tile): boolean {
    if (this.activeOverlay !== null) return false;
    const flow = this.townFlow ?? new TownFlow(this.opts.container);
    this.townFlow = flow;
    const started = flow.start({
      character,
      tile,
      deps: { persistence: this.opts.persistence },
      onResolved: async () => {
        this.activeOverlay = null;
      },
    });
    if (started) this.activeOverlay = 'town';
    return started;
  }

  /** Traveller-merchant (HEX-72): a rare frontier vendor with a higher-rarity
   * shelf, consumed after a single purchase → the tile resolves to a spent
   * marker (repaint so the spent glyph replaces the live ⚑ immediately). */
  private maybeStartMerchant(character: Character, tile: Tile): boolean {
    if (this.activeOverlay !== null) return false;
    const flow = this.merchantFlow ?? new MerchantFlow(this.opts.container);
    this.merchantFlow = flow;
    const started = flow.start({
      character,
      tile,
      deps: { persistence: this.opts.persistence },
      onResolved: async (consumed) => {
        this.activeOverlay = null;
        if (consumed) this.renderer?.updateHex({ q: tile.q, r: tile.r }, tile);
        // HEX-131: merchant purchases spend gold → HUD.
        this.refreshWorldHud(character);
      },
    });
    if (started) this.activeOverlay = 'merchant';
    return started;
  }

  private renderTilesAround(centre: Hex): void {
    const renderer = this.renderer;
    const store = this.worldStore;
    if (!renderer || !store) return;
    // Render a two-ring buffer beyond the centre so both the T1 disk and the
    // T2 fringe (HEX-14) are populated before the next visibility refresh.
    const candidates = neighbourHexesWithin(centre, RENDER_BUFFER_RADIUS);
    for (const hex of candidates) {
      const key = hexKey(hex);
      if (this.renderedHexes.has(key)) continue;
      const tile = store.getTileAt(hex);
      if (!tile) continue;
      renderer.addHex(hex, tile);
      this.renderedHexes.add(key);
    }
  }

  /**
   * Materialise + render mountain tiles within `LANDMARK_RADIUS` of `centre`
   * (HEX-127). Pure render: `addHex` creates the node but credits nothing, and
   * `refreshVision` then paints the cool landmark silhouette for the ones beyond
   * the vision/memory radius. REVEAL-NOT-CREDIT — nothing here touches
   * `Character.explored`, `discoveries`, or any tile's `discoveredBy`. Only
   * `mountain` tiles are surfaced, so the frontier-mystery invariant (content
   * tiles stay `?` until crossed, HEX-85) is untouched.
   */
  private async renderLandmarksAround(centre: Hex): Promise<void> {
    const store = this.worldStore;
    const renderer = this.renderer;
    if (!store || !renderer) return;
    await store.ensureAround(centre, LANDMARK_RADIUS);
    if (this.destroyed) return;
    for (const hex of neighbourHexesWithin(centre, LANDMARK_RADIUS)) {
      const key = hexKey(hex);
      if (this.renderedHexes.has(key)) continue;
      const tile = store.getTileAt(hex);
      if (!tile || tile.type !== 'mountain') continue;
      renderer.addHex(hex, tile);
      this.renderedHexes.add(key);
    }
  }

  private refreshVision(): void {
    const vision = this.vision;
    const renderer = this.renderer;
    const store = this.worldStore;
    if (!vision || !renderer || !store) return;
    const fov = vision.getFoV();
    this.currentVision = new Set(fov.vision);
    // The character has been-in-vision of every hex in the current disk; record
    // it in both the session memory set and the persisted `seen` set (HEX-128) so
    // glimpsed-but-uncrossed tiles survive reload. The render memory set is every
    // seen hex minus this disk.
    for (const key of fov.vision) {
      this.discovered.add(key);
      this.opts.character.seen.add(key);
    }
    const memory = new Set<string>();
    for (const key of this.discovered) {
      if (fov.vision.has(key)) continue;
      const hex = parseHexKey(key);
      if (!hex) continue;
      const tile = store.getTileAt(hex);
      // Every materialised seen tile out of vision lands in memory mode — no
      // longer gated on `showsRealFace` (HEX-128): a discovered / always-visible
      // tile paints its real face, an undiscovered one a remembered `?`. The
      // face split happens in `pickRenderFace`; gating here would drop the latter
      // to black (the bug this fixes).
      if (tile) memory.add(key);
    }
    // Personally-explored set drives the "your map" vs "inherited" visual
    // split inside the active vision disk (ADR-0014 amendment 2026-05-27).
    const personallyExplored: ReadonlySet<string> = this.opts.character.explored;
    // Landmark set (HEX-127): mountains within the skyline scan but beyond the
    // vision disk render as distant silhouettes. Mountains already in `memory`
    // (been beside) take precedence in the renderer — the set may include them
    // harmlessly. Geometric + ephemeral: recomputed each refresh, never folded
    // into `discovered`, so surfacing a peak credits no discovery.
    const landmark = new Set<string>();
    const centre = this.movement?.position;
    if (centre) {
      for (const hex of neighbourHexesWithin(centre, LANDMARK_RADIUS)) {
        const key = hexKey(hex);
        if (fov.vision.has(key)) continue;
        const tile = store.getTileAt(hex);
        if (tile && tile.type === 'mountain') landmark.add(key);
      }
    }
    renderer.setHexVisibility({ vision: fov.vision, memory, personallyExplored, landmark });
  }

  private onHover(event: { q: number; r: number; screenX: number; screenY: number } | null): void {
    this.updateHoverPanel(event);
    // Drive the route preview (HEX-74) off the same hover stream. The renderer
    // emits hover for every hex (not just the vision disk) so a preview can
    // reach out-of-vision memory targets.
    this.hoverHex = event ? { q: event.q, r: event.r } : null;
    this.recomputePreview();
  }

  private updateHoverPanel(
    event: { q: number; r: number; screenX: number; screenY: number } | null,
  ): void {
    const hover = this.hoverPanel;
    const store = this.worldStore;
    if (!hover || !store) return;
    if (event === null) {
      hover.hide();
      return;
    }
    // The panel is vision-scoped (ADR-0014 §5): memory / frontier / undiscovered
    // hexes get no panel even though they now emit hover events for the preview.
    const key = hexKey({ q: event.q, r: event.r });
    if (!this.currentVision.has(key)) {
      hover.hide();
      return;
    }
    const tile = store.getTileAt({ q: event.q, r: event.r });
    if (!tile) {
      hover.hide();
      return;
    }
    // ADR-0014 §5: frontier (`?`-cardback) hexes brighten on hover but do not
    // open the panel — we don't yet know what's there. Always-visible terrain
    // (mountains, landmarks) DOES open the panel even before discovery.
    if (!showsRealFace(tile)) {
      hover.hide();
      return;
    }
    hover.showFor(tile, event.screenX, event.screenY);
  }

  /**
   * Append a route waypoint (HEX-74 shift-click). Accepted only if the leg from
   * the previous endpoint (last waypoint, else current position) pathfinds, so
   * the chain stays fully walkable. A no-op for a duplicate of the last
   * endpoint or an unreachable target. Re-previews the chain on success.
   */
  private addWaypoint(target: Hex): void {
    const movement = this.movement;
    if (!movement) return;
    const last = this.waypoints[this.waypoints.length - 1] ?? movement.position;
    if (target.q === last.q && target.r === last.r) return;
    const seg = findPath(last, target, (hex) => this.isPathWalkable(hex));
    if (!seg || seg.length < 2) return;
    this.waypoints.push(target);
    this.recomputePreview();
  }

  /** Clear pending waypoints and the on-screen preview (HEX-74). */
  private clearWaypoints(): void {
    this.waypoints.length = 0;
    this.clearPreview();
  }

  /** Drop the route-preview highlight (HEX-74). */
  private clearPreview(): void {
    this.renderer?.setPathPreview(null);
  }

  /**
   * Recompute the route preview (HEX-74): the locked waypoint chain plus the
   * tentative leg to the hovered hex. Falls back to the (always-valid) locked
   * chain when the hovered leg is unreachable, and shows nothing while walking
   * or while an overlay owns the screen.
   */
  private recomputePreview(): void {
    const renderer = this.renderer;
    const movement = this.movement;
    if (!renderer || !movement) return;
    if (this.walking || this.activeOverlay !== null) {
      renderer.setPathPreview(null);
      return;
    }
    const endpoints = [...this.waypoints];
    const hover = this.hoverHex;
    if (hover) {
      const last = endpoints[endpoints.length - 1] ?? movement.position;
      if (!(hover.q === last.q && hover.r === last.r)) endpoints.push(hover);
    }
    if (endpoints.length === 0) {
      renderer.setPathPreview(null);
      return;
    }
    const walkable = (hex: Hex): boolean => this.isPathWalkable(hex);
    let path = findPathThrough(movement.position, endpoints, walkable);
    if (!path && this.waypoints.length > 0) {
      path = findPathThrough(movement.position, this.waypoints, walkable);
    }
    renderer.setPathPreview(path && path.length >= 2 ? path : null);
  }

  /** Enter/leave free-look survey mode (HEX-96). Leaving recentres the camera
   *  on the character (renderer snaps back, clearing the pan offset). */
  private setFreeLook(on: boolean): void {
    if (on === this.freeLook) return;
    this.freeLook = on;
    if (on) {
      this.freeLookCue?.show();
    } else {
      this.freeLookCue?.hide();
      this.renderer?.recenterCamera();
    }
  }

  /** Apply a zoom multiplier, clamped to the worldview bounds (HEX-74/144). */
  private zoomBy(factor: number): void {
    const next = clampZoom(this.zoom * factor);
    if (next === this.zoom) return;
    this.zoom = next;
    this.renderer?.setZoom(next);
  }

  private onKey(event: KeyboardEvent): void {
    // Any active overlay (combat / chest / shop / inventory) owns input — the
    // inventory + zoom keys must not fire while a modal is up.
    if (this.activeOverlay !== null) return;
    if (event.key === 'i' || event.key === 'I') {
      event.preventDefault();
      this.openInventory();
      return;
    }
    // Free-look pan mode (HEX-96): `f` toggles a survey mode that decouples the
    // camera from the character; arrow keys pan it past the follow camera's
    // scroll-out; `f` again or Escape recentres. Kept region-local to this
    // input hook + the `freeLook` flag (renderer owns the camera offset).
    if (event.key === 'f' || event.key === 'F') {
      event.preventDefault();
      this.setFreeLook(!this.freeLook);
      return;
    }
    if (this.freeLook) {
      const pan = panDeltaForKey(event.key);
      if (pan) {
        event.preventDefault();
        this.renderer?.panBy(pan.dx, pan.dy);
        return;
      }
      if (event.key === 'Escape') {
        event.preventDefault();
        this.setFreeLook(false);
        return;
      }
    }
    // Keyboard zoom (HEX-74): `+`/`=` zoom in, `-`/`_` zoom out, character-
    // centred — mirrors the scroll-wheel.
    if (event.key === '+' || event.key === '=') {
      event.preventDefault();
      this.zoomBy(ZOOM_STEP);
      return;
    }
    if (event.key === '-' || event.key === '_') {
      event.preventDefault();
      this.zoomBy(1 / ZOOM_STEP);
    }
  }

  destroy(): void {
    if (this.destroyed) return;
    this.destroyed = true;
    if (this.rafHandle !== null) cancelAnimationFrame(this.rafHandle);
    this.rafHandle = null;
    if (this.walkTimer !== null) clearTimeout(this.walkTimer);
    this.walkTimer = null;
    this.walking = false;
    this.clickUnsub?.();
    this.hoverUnsub?.();
    if (this.keyHandler) {
      window.removeEventListener('keydown', this.keyHandler);
      this.keyHandler = null;
    }
    if (this.wheelHandler) {
      this.opts.container.removeEventListener('wheel', this.wheelHandler);
      this.wheelHandler = null;
    }
    if (this.pointerDownHandler) {
      this.opts.container.removeEventListener('pointerdown', this.pointerDownHandler);
      this.pointerDownHandler = null;
    }
    if (this.pointerMoveHandler) {
      this.opts.container.removeEventListener('pointermove', this.pointerMoveHandler);
      this.pointerMoveHandler = null;
    }
    if (this.pointerUpHandler) {
      this.opts.container.removeEventListener('pointerup', this.pointerUpHandler);
      this.opts.container.removeEventListener('pointercancel', this.pointerUpHandler);
      this.pointerUpHandler = null;
    }
    this.dragPan = null;
    this.freeLookCue?.destroy();
    this.hoverPanel?.destroy();
    this.discoveryHud?.destroy();
    this.worldHud?.destroy();
    this.encounterFlow?.destroy();
    this.bossFlow?.destroy();
    this.chestFlow?.destroy();
    this.shopFlow?.destroy();
    this.townFlow?.destroy();
    this.merchantFlow?.destroy();
    this.eventFlow?.destroy();
    this.pillarFlow?.destroy();
    this.inventoryEditor?.destroy();
    this.levelUpOverlay?.destroy();
    this.acquiring?.destroy();
    this.compassHud?.destroy();
    this.freeLookCue = null;
    this.renderer?.destroy();
    this.renderer = null;
    this.worldStore = null;
    this.vision = null;
    this.lifecycle = null;
    this.movement = null;
    this.worldClock = null;
    this.hoverPanel = null;
    this.discoveryHud = null;
    this.worldHud = null;
    this.encounterFlow = null;
    this.bossFlow = null;
    this.chestFlow = null;
    this.shopFlow = null;
    this.townFlow = null;
    this.merchantFlow = null;
    this.eventFlow = null;
    this.pillarFlow = null;
    this.inventoryEditor = null;
    this.levelUpOverlay = null;
    this.acquiring = null;
    this.compassHud = null;
    this.activeOverlay = null;
    this.onCombatExit = null;
    this.renderedHexes.clear();
    this.currentVision.clear();
    this.discovered.clear();
    this.liberatedTowns.clear();
    this.waypoints.length = 0;
    this.hoverHex = null;
  }
}

function neighbourHexesWithin(centre: Hex, radius: number): Hex[] {
  const out: Hex[] = [];
  for (let dq = -radius; dq <= radius; dq++) {
    const drMin = Math.max(-radius, -dq - radius);
    const drMax = Math.min(radius, -dq + radius);
    for (let dr = drMin; dr <= drMax; dr++) {
      out.push({ q: centre.q + dq, r: centre.r + dr });
    }
  }
  return out;
}
